import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useRouterState } from '@tanstack/react-router';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
  Dialog,
  DialogContent,
  DialogTitle,
  Skeleton,
  cn,
} from '@vta/ui';
import type { SearchResult } from '@vta/types';
import { useSearch } from './useSearch.ts';
import { useCommandPaletteHotkey } from './keymap.ts';
import { formatTeamNumber } from '../../lib/format.ts';
import { useCommandPaletteStore } from '../../stores/command-palette-store.ts';

const TYPE_LABEL: Record<SearchResult['type'], string> = {
  team: 'Teams',
  city: 'Cities',
  country: 'Countries',
};

const GROUP_ORDER: Array<SearchResult['type']> = ['team', 'city', 'country'];

export function CommandPalette() {
  const open = useCommandPaletteStore((s) => s.open);
  const setOpen = useCommandPaletteStore((s) => s.setOpen);
  const toggle = useCommandPaletteStore((s) => s.toggle);
  const [query, setQuery] = useState('');
  const navigate = useNavigate();

  useCommandPaletteHotkey(toggle);

  // Reset the query whenever the palette closes so the next open is clean.
  useEffect(() => {
    if (!open) setQuery('');
  }, [open]);

  const search = useSearch(query, open);

  const grouped = useMemo(() => {
    const map = new Map<SearchResult['type'], SearchResult[]>();
    for (const r of search.data?.results ?? []) {
      const list = map.get(r.type) ?? [];
      list.push(r);
      map.set(r.type, list);
    }
    return map;
  }, [search.data]);

  const onAtlas = useRouterState({ select: (s) => s.location.pathname === '/' });

  const handleSelect = useCallback(
    (result: SearchResult) => {
      setOpen(false);
      // SearchResult.href is API-controlled (`/team/839A`, `/city/<slug>` …).
      // Keep navigation type-safe by parsing the prefix.
      //
      // On the atlas (`/`) a team or city pick stays IN the globe: we write
      // the `?team=` / `?city=` search param, which opens the drawer and
      // flies the camera to the marker (PRD §9.3 — search → globe rotates →
      // card opens). Detail routes remain one click away from the drawer.
      if (result.href.startsWith('/team/')) {
        const teamNumber = decodeURIComponent(result.href.slice('/team/'.length));
        if (onAtlas) {
          navigate({
            to: '.',
            from: '/',
            search: (prev: Record<string, unknown>) => ({ ...prev, team: teamNumber, city: undefined }),
            replace: false,
          });
        } else {
          navigate({ to: '/team/$teamNumber', params: { teamNumber } });
        }
      } else if (result.href.startsWith('/city/')) {
        const citySlug = decodeURIComponent(result.href.slice('/city/'.length));
        if (onAtlas) {
          navigate({
            to: '.',
            from: '/',
            search: (prev: Record<string, unknown>) => ({ ...prev, city: citySlug, team: undefined }),
            replace: false,
          });
        } else {
          navigate({ to: '/city/$citySlug', params: { citySlug } });
        }
      } else if (result.href.startsWith('/country/')) {
        navigate({ to: '/country/$countrySlug', params: { countrySlug: decodeURIComponent(result.href.slice('/country/'.length)) } });
      }
    },
    [navigate, onAtlas],
  );

  const trimmed = query.trim();
  const hasResults = (search.data?.results.length ?? 0) > 0;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent
        className="overflow-hidden p-0"
        onOpenAutoFocus={(e) => {
          // Let cmdk autofocus the input itself; Radix's default tries the close button.
          e.preventDefault();
        }}
      >
        <DialogTitle className="sr-only">Search teams, cities, and countries</DialogTitle>
        <Command
          data-vta-command-root="true"
          shouldFilter={false}
          loop
          className="bg-bg-overlay"
        >
          <CommandInput
            value={query}
            onValueChange={setQuery}
            placeholder="Search teams, cities, countries…"
            autoFocus
          />
          <CommandList>
            {trimmed.length === 0 ? (
              <EmptyHint />
            ) : search.isLoading ? (
              <LoadingSkeleton />
            ) : search.isError ? (
              <CommandEmpty>Search failed. Try again.</CommandEmpty>
            ) : !hasResults ? (
              <CommandEmpty>No matches for &ldquo;{trimmed}&rdquo;</CommandEmpty>
            ) : (
              GROUP_ORDER.map((type, idx) => {
                const items = grouped.get(type);
                if (!items?.length) return null;
                return (
                  <div key={type}>
                    {idx > 0 ? <CommandSeparator /> : null}
                    <CommandGroup heading={TYPE_LABEL[type]}>
                      {items.map((item) => (
                        <CommandItem
                          key={`${item.type}:${item.href}`}
                          value={`${item.label}${item.sublabel ? ` ${item.sublabel}` : ''}`}
                          onSelect={() => handleSelect(item)}
                        >
                          <ResultRow item={item} />
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </div>
                );
              })
            )}
          </CommandList>
          <Footer />
        </Command>
      </DialogContent>
    </Dialog>
  );
}

function ResultRow({ item }: { item: SearchResult }) {
  return (
    <div className="flex w-full items-center justify-between gap-3">
      <div className="flex min-w-0 flex-col">
        <span
          className={cn('truncate text-sm text-fg', item.type === 'team' && 'font-mono')}
        >
          {item.type === 'team' ? formatTeamNumber(item.label) : item.label}
        </span>
        {item.sublabel ? (
          <span className="truncate text-xs text-fg-subtle">{item.sublabel}</span>
        ) : null}
      </div>
      <span className="text-2xs uppercase tracking-[0.18em] text-fg-subtle">
        {item.type}
      </span>
    </div>
  );
}

function EmptyHint() {
  return (
    <div className="px-4 py-8 text-center text-sm text-fg-subtle">
      <p className="font-display text-base text-fg">Type to search the atlas</p>
      <p className="mt-2 max-w-[40ch] mx-auto">
        Find a team by number, name, or city. Try{' '}
        <span className="font-mono text-fg-muted">839A</span> or{' '}
        <span className="text-fg-muted">Toronto</span>.
      </p>
    </div>
  );
}

function LoadingSkeleton() {
  return (
    <div className="flex flex-col gap-2 p-2">
      <Skeleton className="h-9 w-full" />
      <Skeleton className="h-9 w-full" />
      <Skeleton className="h-9 w-3/4" />
    </div>
  );
}

function Footer() {
  return (
    <div className="flex items-center justify-between border-t border-border-subtle px-4 py-2 text-2xs uppercase tracking-[0.18em] text-fg-subtle">
      <span>VEX Teams Atlas</span>
      <span className="flex items-center gap-2">
        <Kbd>↑</Kbd>
        <Kbd>↓</Kbd>
        <span>nav</span>
        <Kbd>↵</Kbd>
        <span>open</span>
        <Kbd>esc</Kbd>
        <span>close</span>
      </span>
    </div>
  );
}

function Kbd({ children }: { children: React.ReactNode }) {
  return (
    <kbd className="rounded-xs border border-border-subtle bg-bg-raised px-1.5 py-0.5 text-2xs text-fg-muted">
      {children}
    </kbd>
  );
}
